/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, OnDestroy, Type } from '@angular/core';
import { ChosenWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { AvailableWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { IWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { DashboardChart } from '@src/app/shared/dashboard/dashboard-widget/dashboard-chart';
import { DashboardComponent } from "@src/app/shared/dashboard/dashboard.component";
import { Subscription } from 'rxjs';
import { PostsWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/posts-widget/posts-widget.component';
import { DocumentWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/documents-widget/documents-widget.component';
import { BallotWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/ballot-widget/ballot-widget.component';
import { MyRequestsWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/my-requests-widget/my-requests-widget.component';
import { ReceivablesChart } from '@src/app/pages/omc-dashboard/widgets/receivables-chart';
import { CashflowChart } from '@src/app/pages/omc-dashboard/widgets/cashflow-chart';
import { DebtorsWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/debtors-widget/debtors-widget.component';
import { RequestSummaryWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/request-summary-widget/request-summary-widget.component';
import { SraOmcChart } from '@src/app/pages/omc-dashboard/widgets/SraOmcChart';
import { BankSummaryWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/bank-summary-widget/bank-summary-widget.component';
import { RequestTrendChart } from '@src/app/pages/omc-dashboard/widgets/request-trend-chart';
import { CreditorsWidgetComponent } from '@src/app/pages/omc-dashboard/widgets/creditors-widget/creditors-widget.component';
import { PayablesChart } from '@src/app/pages/omc-dashboard/widgets/payables-chart';

@Component({
    selector: 'app-omc-dashboard',
    imports: [DashboardComponent],
    templateUrl: './omc-dashboard.component.html',
    styleUrl: './omc-dashboard.component.scss'
})
export class OmcDashboardComponent implements OnDestroy {
    dashboardName = 'omcDashboard';
    allWidgets: AvailableWidget[] = [];
    defaultWidgets : ChosenWidget[] = [];

    subscriptions: Subscription[] = [];

    constructor() {
        this.loadCharts();
    }

    available(code: string, name: string, maker: () => DashboardChart) {
        this.allWidgets.push({ code, name, chart: maker(), maker });
    }

    pushWidget(code: string, name: string, widgetClass: Type<IWidget>) {
        this.allWidgets.push({ code, name, widgetClass, chart: null });
    }

    loadCharts() {
        this.allWidgets.length = 0;
        this.pushWidget('receivables-chart', 'Receivables', ReceivablesChart);
        this.pushWidget('bank-summary-widget', 'Bank Summary', BankSummaryWidgetComponent);
        this.pushWidget('cashflow-chart', 'Cashflow', CashflowChart);
        this.pushWidget('debtors-widget', 'Debtors', DebtorsWidgetComponent);
        this.pushWidget('payables-chart', 'Payables', PayablesChart);
        this.pushWidget('creditors-widget', 'Creditors', CreditorsWidgetComponent);
        this.pushWidget('request-status-chart', 'Request Status', SraOmcChart);
        this.pushWidget('request-trend-chart', 'Request Trend', RequestTrendChart);
        this.pushWidget('request-summary-widget', 'Request Summary', RequestSummaryWidgetComponent);
        this.pushWidget('posts-widget', 'Recent Posts Widget', PostsWidgetComponent);
        this.pushWidget('documents-widget', 'Recent Documents', DocumentWidgetComponent);
        this.pushWidget('ballots-widget', 'Recent Votes', BallotWidgetComponent);        
        this.pushWidget('my-requests-widget', 'My Requests', MyRequestsWidgetComponent);

        // Initialize defaults, should check privs first!
        for (let i = 0; i < Math.min(8, this.allWidgets.length); i++) {
            const w = this.allWidgets[i];
            this.defaultWidgets.push({code: w.code, widthWeight: 2, heightWeight: 2});
        }        
    }

    ngOnDestroy(): void {
      this.allWidgets.forEach ( aw => aw.chart = null);
      this.subscriptions.forEach(s => s.unsubscribe());
    }
}
